import { useRef } from "react";
import { motion, useMotionValue, useTransform, useScroll } from "framer-motion";
import { MapPin, BadgeCheck } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1607336370849-990004549d6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtYXlmYWlyJTIwbG9uZG9uJTIwdG93bmhvdXNlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4OTE1NTEyNXww&ixlib=rb-4.1.0&q=85";

const APP_URL = "https://app.primeconnect.network";

const LINES = [
  <>The private network</>,
  <>for <em className="italic font-light">prime residential</em></>,
  <>agents.</>,
];

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.45 } },
};

const lineInner = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-5, 5]);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMouseMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="relative overflow-hidden pt-36 sm:pt-44 pb-16 sm:pb-24"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0.25}
            className="font-eyebrow text-[11px] text-[#928E84] mb-8"
          >
            Now Open to Founding Members
          </motion.p>

          <motion.h1
            variants={lineWrap}
            initial="hidden"
            animate="show"
            className="font-display font-medium tracking-tight text-[#141518] text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02]"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span variants={lineInner} className="block">
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1.15}
            className="mt-8 max-w-xl text-base sm:text-lg text-[#635F57] leading-relaxed"
          >
            PrimeConnect matches your listings and client searches with
            trusted agents at other prime agencies — in your city and
            internationally — and settles the commission split in-app. Nothing is
            ever published to a portal.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1.35}
            className="mt-10"
          >
            <a
              href={APP_URL}
              data-testid="hero-join-btn"
              className="inline-flex items-center rounded-full bg-[#141518] text-white text-sm font-medium px-9 py-4 hover:bg-[#26282E] hover:-translate-y-0.5 transition-all duration-300"
            >
              Join PrimeConnect
            </a>
            <p className="text-sm text-[#635F57] mt-5">
              Every member is verified as a working agent.
            </p>
            <p className="text-xs text-[#928E84] mt-2">
              iPhone and Android apps coming soon.
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-5 [perspective:1200px]">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{ rotateX, rotateY, y: cardY }}
            className="animate-floaty"
          >
            <div
              data-testid="hero-listing-card"
              className="bg-white border border-[#E7E4DC] rounded-3xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(20,21,24,0.25)]"
            >
              <div className="relative h-60 sm:h-72 overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="Prime Mayfair townhouse"
                  className="w-full h-full object-cover scale-105"
                />
                <span className="absolute top-4 left-4 font-eyebrow text-[10px] bg-white/95 text-[#141518] px-3.5 py-1.5 rounded-full">
                  To Let
                </span>
                <span className="absolute top-4 right-4 font-eyebrow text-[10px] bg-[#141518]/90 text-white px-3.5 py-1.5 rounded-full flex items-center gap-1.5">
                  <BadgeCheck size={12} /> Off-Market
                </span>
              </div>
              <div className="p-7">
                <p className="font-display text-3xl text-[#141518]">£50,000/mo</p>
                <p className="text-base font-medium text-[#141518] mt-1">4 bedroom house</p>
                <p className="text-sm text-[#928E84] mt-1 flex items-center gap-1.5">
                  <MapPin size={13} /> Queen&rsquo;s Gate Terrace, South Kensington
                </p>
                <div className="mt-6 pt-5 border-t border-[#E7E4DC] flex items-center justify-between">
                  <div>
                    <p className="font-eyebrow text-[9px] text-[#928E84]">Cross-agency match</p>
                    <p className="text-xs font-medium text-[#141518] mt-1.5">
                      Your agency &times; Partner agency
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-eyebrow text-[9px] text-[#928E84]">Agreed split</p>
                    <p className="font-display text-xl text-[#B89B6C] mt-0.5">3%</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
